'use client';

import { useMemo } from 'react';
import { useSearch } from '@/app/search-context';
import { MeasurementFilters, filterModels, parseCup } from './actions/list';
import { CUP_ORDER } from '@/constants';
import RangeSlider from './range-slider-client';
import { ModelDisplay } from '@/shared';

function getDataRange(
  models: ModelDisplay[],
  measurement: keyof MeasurementFilters
): [number, number] | null {
  const values: number[] = [];

  for (const m of models) {
    if (measurement === `band` || measurement === `cup`) {
      const parsed = parseCup(m.cup);
      if (measurement === `band` && parsed.band != null) values.push(parsed.band);
      if (measurement === `cup` && parsed.letter) {
        const idx = CUP_ORDER.indexOf(parsed.letter);
        if (idx !== -1) values.push(idx);
      }
    } else {
      const v = m[measurement];
      if (v != null) values.push(v);
    }
  }

  if (values.length === 0) return null;
  return [Math.min(...values), Math.max(...values),];
}

function getAvailableCups(models: ModelDisplay[]): number[] {
  const indices = new Set<number>();
  for (const m of models) {
    const parsed = parseCup(m.cup);
    if (parsed.letter) {
      const idx = CUP_ORDER.indexOf(parsed.letter);
      if (idx !== -1) indices.add(idx);
    }
  }
  return Array.from(indices).sort((a, b) => a - b);
}

export default function FilterMeasurementClient({
  measurement,
  className,
}: {
  measurement: keyof MeasurementFilters,
  className?: string,
}) {
  const { allModels, measurementFilters, setMeasurementFilter, dataLoading, pageQuery, modelSort, selectedModelTags, } = useSearch();

  // Filter models by everything EXCEPT this measurement's filter
  const filteredModels = useMemo(() => {
    if (!allModels) return null;
    const otherMeasurements = { ...measurementFilters, [measurement]: null, };
    return filterModels(allModels, pageQuery, modelSort, selectedModelTags, otherMeasurements);
  }, [allModels, measurementFilters, measurement, pageQuery, modelSort, selectedModelTags,]);

  // Full range from all models (for fallback when filtered range is empty)
  const fullRange = useMemo(() => {
    if (!allModels) return null;
    return getDataRange(allModels, measurement);
  }, [allModels, measurement,]);

  const filteredRange = useMemo(() => {
    if (!filteredModels) return null;
    return getDataRange(filteredModels, measurement);
  }, [filteredModels, measurement,]);

  // For cup: only show values that exist in the data
  const availableCups = useMemo(() => {
    if (measurement !== `cup`) return null;
    return getAvailableCups(filteredModels ?? allModels ?? []);
  }, [measurement, filteredModels, allModels,]);

  if (!fullRange || dataLoading) {
    return (
      <div className={`filter-measurement filter-measurement-loading ${className ?? ``}`}>
        <div className="range-slider">
          <div className="range-slider-skeleton" />
        </div>
      </div>
    );
  }

  // Cup uses local indices into available cups array
  const isCup = measurement === `cup` && availableCups && availableCups.length > 0;
  const cupList = isCup ? availableCups : null;

  const noResults = !filteredRange && !isCup;
  const sliderMin = cupList ? 0 : (filteredRange ?? fullRange)[0];
  const sliderMax = cupList ? cupList.length - 1 : (filteredRange ?? fullRange)[1];
  const singleValue = sliderMin === sliderMax;

  const currentFilter = measurementFilters[measurement];
  let current: [number, number];
  if (cupList && currentFilter) {
    // Map CUP_ORDER indices back to local indices
    const localMin = cupList.findIndex(c => c >= currentFilter[0]);
    const localMax = cupList.length - 1 - [...cupList,].reverse().findIndex(c => c <= currentFilter[1]);
    current = [Math.max(localMin === -1 ? 0 : localMin, 0), Math.min(localMax, cupList.length - 1),] satisfies [number, number];
  } else if (currentFilter && !cupList) {
    current = [Math.max(currentFilter[0], sliderMin), Math.min(currentFilter[1], sliderMax),] satisfies [number, number];
  } else {
    current = [sliderMin, sliderMax,] satisfies [number, number];
  }

  const formatValue = cupList
    ? (v: number) => CUP_ORDER[cupList[v]] ?? String(v)
    : undefined;

  return (
    <div className={`filter-measurement ${className ?? ``}`}>
      <RangeSlider
        min={sliderMin}
        max={sliderMax}
        value={current}
        onChange={(range) => {
          if (cupList) {
            // Convert local indices back to CUP_ORDER indices
            const cupMin = cupList[range[0]];
            const cupMax = cupList[range[1]];
            const isDefault = range[0] === 0 && range[1] === cupList.length - 1;
            setMeasurementFilter(measurement, isDefault ? null : [cupMin, cupMax,]);
          } else {
            const isDefault = range[0] === sliderMin && range[1] === sliderMax;
            setMeasurementFilter(measurement, isDefault ? null : range);
          }
        }}
        formatValue={formatValue}
        disabled={noResults || singleValue}
      />
    </div>
  );
}
